"use client";

import {
  Box,
  Flex,
  Heading,
  Separator,
  Spinner,
  Text,
  Timeline,
} from "@chakra-ui/react";

import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";
import { LuCheck, LuClock } from "react-icons/lu";
import { lookupTravelApplicationAction } from "server/actions/travel";
import { useOrgId } from "hooks/useOrgId";
import {
  filterAndSortServices,
  formatServiceLabel,
  SERVICE_TIMELINES,
} from "@features/services/travel/travelServiceEvents";
import type { TravelServiceType } from "@features/services/travel/travelServiceEvents";
import { formatTimestamp } from "helpers/formatTimestamp";

interface ServiceData {
  id: number;
  serviceType: string;
  events: { event: string; timestamp: Date }[];
}

interface ApplicationData {
  trackingRef: string;
  customerName: string | null;
  packageType: string;
  durationDays: number | null;
  createdAt: Date;
  services: ServiceData[];
  participants: { id: number; name: string; sortOrder: number }[];
}

const ServiceTimeline = ({ service }: { service: ServiceData }) => {
  const timeline =
    SERVICE_TIMELINES[service.serviceType as TravelServiceType] ?? [];
  const completedMap = new Map(
    service.events.map((e) => [e.event, e.timestamp]),
  );

  return (
    <Flex flexDirection="column" gap={3}>
      <Text fontSize="sm" fontWeight="semibold" color="fg">
        {formatServiceLabel(service.serviceType)}
      </Text>
      <Timeline.Root size="sm">
        {timeline.map((step) => {
          const timestamp = completedMap.get(step.id);
          const isCompleted = timestamp != null;

          return (
            <Timeline.Item key={step.id}>
              <Timeline.Connector>
                <Timeline.Separator borderColor="border.light" />
                <Timeline.Indicator
                  bg={isCompleted ? "green.solid" : "transparent"}
                  color={isCompleted ? "green.contrast" : "fg.muted"}
                >
                  {isCompleted ? <LuCheck /> : <LuClock />}
                </Timeline.Indicator>
              </Timeline.Connector>
              <Timeline.Content>
                <Flex flexDirection="column">
                  <Timeline.Title
                    color={isCompleted ? "text.primary" : "text.secondary"}
                  >
                    {step.label}
                  </Timeline.Title>
                  {isCompleted && (
                    <Timeline.Description>
                      {formatTimestamp(timestamp)}
                    </Timeline.Description>
                  )}
                </Flex>
              </Timeline.Content>
            </Timeline.Item>
          );
        })}
      </Timeline.Root>
    </Flex>
  );
};

const TravelTrackingView = ({
  trackingRef,
  onBack,
}: {
  trackingRef: string;
  onBack: () => void;
}) => {
  const orgId = useOrgId();

  type ViewState =
    | { status: "loading" }
    | { status: "not_found" }
    | { status: "loaded"; data: ApplicationData };

  const [viewState, setViewState] = useState<ViewState>({ status: "loading" });

  useEffect(() => {
    setViewState({ status: "loading" });
    lookupTravelApplicationAction(orgId, trackingRef).then((result) => {
      if (result == null) {
        setViewState({ status: "not_found" });
      } else {
        setViewState({ status: "loaded", data: result });
      }
    });
  }, [trackingRef]);

  if (viewState.status === "loading") {
    return (
      <Flex justify="center" align="center" py={16}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (viewState.status === "not_found") {
    return (
      <Flex flexDirection="column" align="center" gap={4} py={16}>
        <Heading size="lg" color="fg">
          Not found
        </Heading>
        <Text color="text.secondary" fontSize="sm">
          No application found for tracking reference{" "}
          <Box as="span" fontWeight="bold">
            {trackingRef}
          </Box>
          .
        </Text>
        <Text
          as="button"
          fontSize="sm"
          color="fg.muted"
          cursor="pointer"
          textDecoration="underline"
          onClick={onBack}
        >
          Back to home
        </Text>
      </Flex>
    );
  }

  const application = viewState.data;

  const packageLabel =
    application.packageType === "premium"
      ? "Premium"
      : application.packageType === "custom"
        ? "Custom"
        : "Standard";

  return (
    <Flex flexDirection="column" gap={6}>
      <Flex flexDirection="column" gap={1}>
        <Text
          as="button"
          fontSize="sm"
          color="fg.muted"
          cursor="pointer"
          textDecoration="underline"
          alignSelf="flex-start"
          onClick={onBack}
        >
          Back to home
        </Text>
        <Flex gap={4} align="flex-start" justify="space-between">
          <Flex flexDirection="column" gap={1}>
            <Heading size="lg" color="fg">
              {trackingRef}
            </Heading>
            <Flex gap={3} flexWrap="wrap" align="baseline">
              {application.customerName != null && (
                <Text fontSize="md" fontWeight="medium" color="text.primary">
                  {application.customerName}
                </Text>
              )}
              <Text fontSize="md" fontWeight="medium" color="text.primary">
                {packageLabel} package
              </Text>
              {application.durationDays != null && (
                <Text fontSize="md" fontWeight="medium" color="text.primary">
                  {application.durationDays} days
                </Text>
              )}
            </Flex>
            <Text fontSize="sm" color="text.secondary">
              Submitted {formatTimestamp(application.createdAt)}
            </Text>
          </Flex>
          <Box flexShrink={0} p={2} bg="white" borderRadius="md">
            <QRCodeSVG
              value={`${window.location.origin}/scan?service=travel&ref=${trackingRef}`}
              size={80}
            />
          </Box>
        </Flex>
      </Flex>

      <Separator borderColor="border.dark" />

      {application.services.length === 0 && (
        <Text color="text.secondary" fontSize="sm">
          No services found for this application.
        </Text>
      )}

      <Flex flexDirection="column" gap={6}>
        {filterAndSortServices(application.services).map((service) => (
          <ServiceTimeline key={service.id} service={service} />
        ))}
      </Flex>

      {application.participants.length > 0 && (
        <>
          <Separator borderColor="border.dark" />
          <Flex flexDirection="column" gap={2}>
            <Text fontSize="sm" fontWeight="semibold" color="fg">
              Participants
            </Text>
            {application.participants.map((p) => (
              <Text key={p.id} fontSize="sm" color="text.secondary">
                {p.sortOrder + 1}. {p.name}
              </Text>
            ))}
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default TravelTrackingView;
